// ============================================================
// SpringBootDTO.jsx
//
// A branded, responsive course content page for the
// "Using DTOs in Spring Boot" lesson.
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: Data Transfer Object pattern, ModelMapper logic,
//           and architectural boundary visualization.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import SecurityIcon           from "@mui/icons-material/Security";
import TransformIcon          from "@mui/icons-material/Transform";
import ShieldIcon             from "@mui/icons-material/Shield";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import FilterAltIcon          from "@mui/icons-material/FilterAlt";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function SpringBootDTO() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 12"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Data Transfer <span>Objects (DTO)</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Isolate your internal domain models from external API contracts. DTOs serve as a protective barrier, preventing the overexposure of sensitive data and providing a flexible way to shape responses for client consumption.
                    </Typography>
                </Box>

                {/* 1. WHY DTO? */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ShieldIcon />} label="1. The Security Barrier" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Exposing JPA Entities directly can lead to security vulnerabilities (like Mass Assignment) and performance issues (circular references). DTOs allow you to explicitly define what the client sees.
                    </Typography>
                    <CodeBlock
                        title="Decoupled Response Logic"
                        code={`// Return DTO instead of the raw Entity\n@GetMapping("/users/{id}")\npublic UserDto getUser(@PathVariable Long id) {\n    User user = userService.findById(id);\n    return userMapper.toDto(user); \n}`}
                    />
                </Box>

                {/* 2. MAPPING STRATEGIES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TransformIcon />} label="2. Object Mapping" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        While manual mapping is safer for small projects, libraries like <strong>ModelMapper</strong> or <strong>MapStruct</strong> can automate the conversion between Entities and DTOs.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                                <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1, fontSize: 14 }}>Manual Mapping</Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>Explicitly set fields. High control, zero overhead, but verbose for large objects.</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.gold}`, borderRadius: 2 }}>
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 700, mb: 1, fontSize: 14 }}>Automated Mapping</Typography>
                                <Typography sx={{ color: BRAND.white, fontSize: 12 }}>Use ModelMapper.map() or MapStruct interfaces to reduce boilerplate significantly.</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* 3. FLATTENING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<FilterAltIcon />} label="3. Response Flattening" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        DTOs are excellent for "flattening" deep object hierarchies into a single, simple object that is easier for frontend developers to consume.
                    </Typography>
                    <CodeBlock
                        title="Entity to Flattened DTO"
                        code={`public class UserDto {\n    private String name;\n    private String email;\n    // Derived from nested User.Address.getCity()\n    private String city; \n}`}
                    />
                </Box>

                {/* 4. VALIDATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="4. Input Validation Contracts" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Use DTOs to enforce strict validation rules on incoming request bodies without cluttering your database entity annotations.
                    </Typography>
                    <CodeBlock
                        title="Validated Request DTO"
                        code={`public class RegisterRequest {\n    @NotBlank(message = "Username is required")\n    private String username;\n\n    @Email(message = "Invalid format")\n    private String email;\n}`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="DTO Best Practices" />
                    <Grid container spacing={1.5}>
                        {[
                            "Isolate internal Database Entities from public API JSON",
                            "Prevent over-fetching by only including necessary fields",
                            "Use DTOs to aggregate data from multiple entities",
                            "Apply Jakarta Bean Validation specifically to Request DTOs",
                            "Consider using Java Records for immutable, clean DTOs",
                            "Avoid business logic inside DTOs; keep them as pure data carriers"
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> In modern Java (17+), use <strong>Records</strong> for your DTOs. They are immutable by default, generate constructors/getters automatically, and lead to significantly cleaner code.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
